import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Badge,
  Button,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { personnelAPI } from '../../helpers/api';

interface SitePersonnelEntry {
  id: string;
  assignedAt: string;
  removedAt: string | null;
  isActive: boolean;
  site: {
    id: string;
    name: string;
    code: string;
    project: {
      id: string;
      name: string;
      code: string;
      city: { name: string };
    };
  };
}

interface Personnel {
  id: string;
  firstName: string;
  lastName: string;
  tcNo: string | null;
  phone: string | null;
  title: string | null;
  dailyWage: number | null;
  isActive: boolean;
  startDate: string | null;
  sitePersonnel: SitePersonnelEntry[];
}

const PersonnelDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [personnel, setPersonnel] = useState<Personnel | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await personnelAPI.getOne(id!);
      setPersonnel(response.data);
    } catch (error) {
      console.error('Data fetch error:', error);
      toast.error('Personel bilgileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Çalışma süresini hesapla
  const calculateDuration = (startDate: string, endDate: string | null): string => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 30) {
      return `${diffDays} gün`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      const days = diffDays % 30;
      return days > 0 ? `${months} ay ${days} gün` : `${months} ay`;
    } else {
      const years = Math.floor(diffDays / 365);
      const remainingDays = diffDays % 365;
      const months = Math.floor(remainingDays / 30);
      return months > 0 ? `${years} yıl ${months} ay` : `${years} yıl`;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-2">Yükleniyor...</p>
      </div>
    );
  }

  if (!personnel) {
    return (
      <div className="text-center py-5">
        <p className="text-danger">Personel bulunamadı</p>
        <Button color="primary" onClick={() => navigate(-1)}>
          Geri Dön
        </Button>
      </div>
    );
  }

  // Aktif ve geçmiş şantiyeler
  const activeSites = personnel.sitePersonnel.filter((sp) => sp.isActive);
  const pastSites = personnel.sitePersonnel.filter((sp) => !sp.isActive);

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-1">Personel Detayı</h4>
            <div className="page-title-right">
              <Button color="outline-secondary" onClick={() => navigate(-1)}>
                <i className="ri-arrow-left-line me-1"></i>
                Geri
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Personel Bilgileri */}
      <Row>
        <Col lg={4}>
          <Card>
            <CardHeader>
              <h5 className="card-title mb-0">
                <i className="ri-user-3-line me-2"></i>
                Personel Bilgileri
              </h5>
            </CardHeader>
            <CardBody>
              <div className="d-flex align-items-center mb-4">
                <div
                  className="avatar-lg rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                  style={{ width: 64, height: 64, fontSize: '1.5rem' }}
                >
                  {personnel.firstName[0]}{personnel.lastName[0]}
                </div>
                <div className="ms-3">
                  <h5 className="mb-1">
                    {personnel.firstName} {personnel.lastName}
                  </h5>
                  <Badge color={personnel.isActive ? 'success' : 'secondary'}>
                    {personnel.isActive ? 'Aktif' : 'Pasif'}
                  </Badge>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-borderless mb-0">
                  <tbody>
                    <tr>
                      <td className="text-muted" style={{ width: 100 }}>Ünvan</td>
                      <td><strong>{personnel.title || 'İşçi'}</strong></td>
                    </tr>
                    <tr>
                      <td className="text-muted">TC No</td>
                      <td>{personnel.tcNo || '-'}</td>
                    </tr>
                    <tr>
                      <td className="text-muted">Telefon</td>
                      <td>
                        {personnel.phone ? (
                          <a href={`tel:${personnel.phone}`}>{personnel.phone}</a>
                        ) : '-'}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Yevmiye</td>
                      <td>
                        {personnel.dailyWage
                          ? <strong className="text-success">₺{personnel.dailyWage.toLocaleString('tr-TR')}</strong>
                          : '-'}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">İşe Giriş</td>
                      <td>
                        {personnel.startDate
                          ? new Date(personnel.startDate).toLocaleDateString('tr-TR')
                          : '-'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col lg={8}>
          {/* Aktif Şantiyeler */}
          <Card>
            <CardHeader>
              <h5 className="card-title mb-0">
                <i className="ri-building-line me-2 text-success"></i>
                Aktif Çalıştığı Şantiyeler
                <Badge color="success" className="ms-2">{activeSites.length}</Badge>
              </h5>
            </CardHeader>
            <CardBody>
              {activeSites.length === 0 ? (
                <div className="text-center py-4">
                  <i className="ri-building-line text-muted" style={{ fontSize: '2rem' }}></i>
                  <p className="text-muted mt-2 mb-0">
                    Bu personel şu anda hiçbir şantiyeye atanmamış
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Şantiye</th>
                        <th>Proje</th>
                        <th>Şehir</th>
                        <th>Atanma Tarihi</th>
                        <th>Süre</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeSites.map((sp) => (
                        <tr key={sp.id}>
                          <td>
                            <strong>{sp.site.name}</strong>
                            <small className="text-muted d-block">{sp.site.code}</small>
                          </td>
                          <td>{sp.site.project.name}</td>
                          <td>{sp.site.project.city.name}</td>
                          <td>{new Date(sp.assignedAt).toLocaleDateString('tr-TR')}</td>
                          <td>
                            <Badge color="success">
                              {calculateDuration(sp.assignedAt, null)}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Geçmiş Şantiyeler */}
          {pastSites.length > 0 && (
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">
                  <i className="ri-history-line me-2 text-secondary"></i>
                  Çalışma Geçmişi
                  <Badge color="secondary" className="ms-2">{pastSites.length}</Badge>
                </h5>
              </CardHeader>
              <CardBody>
                <div className="table-responsive">
                  <Table className="table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Şantiye</th>
                        <th>Proje</th>
                        <th>Şehir</th>
                        <th>Başlangıç</th>
                        <th>Bitiş</th>
                        <th>Toplam Süre</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pastSites.map((sp) => (
                        <tr key={sp.id}>
                          <td>
                            <strong>{sp.site.name}</strong>
                            <small className="text-muted d-block">{sp.site.code}</small>
                          </td>
                          <td>{sp.site.project.name}</td>
                          <td>{sp.site.project.city.name}</td>
                          <td>{new Date(sp.assignedAt).toLocaleDateString('tr-TR')}</td>
                          <td>
                            {sp.removedAt
                              ? new Date(sp.removedAt).toLocaleDateString('tr-TR')
                              : '-'}
                          </td>
                          <td>
                            <Badge color="secondary">
                              {calculateDuration(sp.assignedAt, sp.removedAt)}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
    </>
  );
};

export default PersonnelDetail;
